import { parseValidHttpUrl } from "./parseValidHttpUrl";

export type ContextDeviceInfo = {
  userAgent: string | null;
  ip: string | null;
  ipCountry: string | null;
  ipCity: string | null;
  ipState: string | null;
  ipZip: string | null;
  referer: string | null;
  sourceUrl?: string;
  gpc?: boolean | null;
};

export type DeviceInfo = {
  isMobile: boolean;
  isDesktop: boolean;
};

/** Fallback ZIP when Cloudflare geolocation is unavailable (e.g., non-US visitor). */
export const FALLBACK_ZIP = "75001";

export const getCfGeolocationDataFromHeaders = (headers: Headers) => ({
  cfCountry: getCfCountry(headers),
  cfCity: getCfCity(headers),
  cfState: getCfState(headers),
  cfZip: getCfZip(headers),
});

export const getCfCountry = (headers: Headers) =>
  (headers.get("test-cf-ipcountry") ?? headers.get("cf-ipcountry")) as string | undefined;

export const getCfCity = (headers: Headers) =>
  (headers.get("test-cf-ipcity") ?? headers.get("cf-ipcity")) as string | undefined;

export const getCfState = (headers: Headers) =>
  (headers.get("test-cf-region-code") ?? headers.get("cf-region-code")) as string | undefined;

export const getCfZip = (headers: Headers) =>
  (headers.get("test-cf-postal-code") ?? headers.get("cf-postal-code")) as string | undefined;

/**
 * Resolve the client device info from request headers.
 *
 * `attestedIp` (optional) takes precedence over every header-based IP. Pass it
 * when the request carried a valid SSR attestation (see ssrAttestation.ts) —
 * the attested IP is the only trustworthy way to identify the real visitor for
 * internal pod-to-pod SSR calls, where `cf-connecting-ip` is absent and
 * `x-real-ip` / `x-forwarded-for` would otherwise be spoofable.
 */
export const getDeviceInfoFromHeaders = (
  headers: Headers,
  attestedIp?: string | null
): ContextDeviceInfo => {
  const userAgent = headers.get("user-agent") as string | undefined;
  const forwardedForIp = headers.get("x-forwarded-for")?.split(",")[0]?.trim();
  const ip =
    attestedIp ||
    headers.get("cf-connecting-ip") ||
    headers.get("x-real-ip") ||
    forwardedForIp;

  const {
    cfCountry: ipCountry,
    cfCity: ipCity,
    cfState: ipState,
    cfZip: ipZip,
  } = getCfGeolocationDataFromHeaders(headers);

  return {
    sourceUrl: parseValidHttpUrl(headers.get("x-dream-href") || headers.get("referer")) || undefined,
    gpc: headers.get("sec-gpc") === "1" || headers.get("x-dream-gpc") === "true"
      ? true
      : headers.get("sec-gpc") === "0" || headers.get("x-dream-gpc") === "false"
        ? false
        : null,
    userAgent: userAgent || null,
    ip: ip || null,
    ipCountry: ipCountry || null,
    ipCity: ipCity || null,
    ipState: ipState || null,
    ipZip: ipZip || null,
    referer: headers.get("referer") || headers.get("referrer") || null,
  };
};
